/**
 * WebGL2Backend — GPU implementation of Mertens exposure fusion that mirrors
 * `src/pipeline/hdr/mertens.js` numerically. Output Float32 RGBA must match
 * the CPU path within maxΔ ≤ 4e-3 in [0,1] per `test:hdr-gpu-parity`.
 *
 * Architecture (per-bracket-per-level passes — see `gpu/shaders.js`):
 *
 *   1. Per bracket: upload RGBA32F texture → compute raw weights (R32F).
 *   2. Sum raw weights across brackets via additive blending → sum_w (R32F).
 *   3. Per bracket: divide raw_w / (sum_w + EPS) → normalized w (R32F).
 *      Build Gaussian-of-weight pyramid (one R32F texture per level) by
 *      separable blur+stride-2 downsample.
 *      Build Gaussian-of-RGBA pyramid (one RGBA32F texture per level) the
 *      same way. Then derive Laplacian: L_i = G_i - upsample(G_{i+1}); top
 *      level keeps Gaussian residual.
 *      Accumulate weighted-Laplacian into the merged pyramid via additive
 *      blending (ONE/ONE).
 *   4. Collapse merged pyramid coarse → fine: per level upsample the
 *      previous accumulator and add to current merged level.
 *   5. Force alpha = 1 on L0 and read back via gl.readPixels into a fresh
 *      Float32Array.
 *
 * Resource lifecycle:
 *
 *   - GL programs and the shared full-screen-triangle VAO are created once
 *     at initialize() and re-used across calls.
 *   - Textures and FBOs allocated lazily per fuseBrackets() call and freed
 *     before return. Returning aborts also free everything via the catch
 *     path. A failed initialize() calls dispose() so the backend can be
 *     recreated cleanly.
 *
 * Cancellation: `signal.aborted` is checked between brackets and between
 * collapse levels. We do NOT pre-empt mid-pass — a single GPU draw on the
 * test image is sub-millisecond and would only complicate state cleanup.
 */

import {
    VERTEX_FULLSCREEN,
    FRAG_BLUR_H,
    FRAG_DOWNSAMPLE_V,
    FRAG_BLUR_V,
    FRAG_UPSAMPLE_H,
    FRAG_WEIGHTS,
    FRAG_LUMINANCE_R,
    FRAG_PRODUCT_R,
    FRAG_MEDIAN_RGBA,
    FRAG_MAXW_R,
    FRAG_HYBRID_TOP,
    FRAG_BOX_H_R,
    FRAG_BOX_V_R,
    FRAG_GUIDED_AB,
    FRAG_GUIDED_OUTPUT,
    FRAG_EXTRACT_CHANNEL_R,
    FRAG_COPY_R,
    FRAG_NORMALIZE_W,
    FRAG_LAPLACIAN_DIFF,
    FRAG_WEIGHTED_LAP,
    FRAG_COLLAPSE_ADD,
    FRAG_FINALIZE,
} from './shaders.js';

import { ingestSRGB } from '../../ingest.js';
import {
    DEFAULT_MU_EV,
    DEFAULT_SIGMA_EV,
    DEFAULT_EV_FLOOR,
} from '../mertens.js';
import {
    selectGuideIndex,
    defaultRadius as defaultGuidedRadius,
    clampRadius as clampGuidedRadius,
    DEFAULT_GUIDED_EPS,
} from '../guided-filter.js';

const DEFAULT_LEVELS = 5;
const MIN_LEVELS = 3;
const MAX_LEVELS = 7;
/* Codex R7 fix: must mirror CPU's MIN_DIM exactly (src/pipeline/hdr/mertens.js).
 * Otherwise small-image inputs (e.g. 10×10) build different pyramid depths on
 * CPU vs GPU and parity diverges silently. Both sides relaxed to 2. */
const MIN_DIM = 2;

function clampLevels(levels) {
    if (levels == null) return DEFAULT_LEVELS;
    const n = levels | 0;
    if (n < MIN_LEVELS) return MIN_LEVELS;
    if (n > MAX_LEVELS) return MAX_LEVELS;
    return n;
}

/**
 * Mirror Lane A's pyramid dimension chain exactly. Level 0 is the input
 * size; each subsequent level is ((w+1)>>1, (h+1)>>1). Stops when either
 * dimension would drop below MIN_DIM, capped at `levels`.
 */
function pyramidDims(width, height, levels) {
    const dims = [];
    let w = width, h = height;
    for (let L = 0; L < levels; L++) {
        dims.push({ w, h });
        if (L === levels - 1) break;
        const nw = (w + 1) >> 1;
        const nh = (h + 1) >> 1;
        if (nw < MIN_DIM || nh < MIN_DIM) break;
        w = nw; h = nh;
    }
    return dims;
}

function debugLog(...args) {
    /* Guarded — only fires when caller opts in via window.BLRRD_DEBUG_GPU. */
    if (typeof globalThis !== 'undefined' && globalThis.BLRRD_DEBUG_GPU) {
        /* eslint-disable-next-line no-console */
        console.log('[BlrrdHDR/GPU]', ...args);
    }
}

export class WebGL2Backend {
    constructor(canvas) {
        /* Canvas: caller's choice (e.g. an OffscreenCanvas). If absent, we
         * try to allocate one ourselves; this works in both window and
         * worker contexts modulo OffscreenCanvas availability. */
        this.canvas = canvas || null;
        this.gl = null;
        this.precision = null;     // '32f' | '16f' once initialised
        this.colorBufferFloat = null;
        this.programs = {};        // operator-name → WebGLProgram
        this.uniforms = {};        // operator-name → { uniformName: location, ... }
        this.vao = null;
        this.disposed = false;
    }

    /**
     * Compile shaders, allocate VAO. Returns true on success. On failure,
     * logs to debug + calls dispose() so the instance is safe to garbage
     * collect — caller should fall back to CPU path.
     */
    initialize() {
        if (this.gl) return true;
        try {
            const canvas = this.canvas || createOffscreenCanvas(2, 2);
            this.canvas = canvas;
            const gl = canvas.getContext('webgl2', {
                alpha: false,
                antialias: false,
                premultipliedAlpha: false,
                preserveDrawingBuffer: false,
                powerPreference: 'high-performance',
                desynchronized: false,
            });
            if (!gl) {
                debugLog('webgl2 unavailable');
                return false;
            }
            this.gl = gl;

            /* Float color-buffer support — REQUIRED. We try RGBA32F first
             * (EXT_color_buffer_float in WebGL2). If unavailable, fall back
             * to RGBA16F (EXT_color_buffer_half_float). If neither, abort. */
            const ext32 = gl.getExtension('EXT_color_buffer_float');
            const ext16 = gl.getExtension('EXT_color_buffer_half_float')
                       || gl.getExtension('EXT_color_buffer_half_float_2');
            if (ext32) {
                this.precision = '32f';
                this.colorBufferFloat = ext32;
            } else if (ext16) {
                this.precision = '16f';
                this.colorBufferFloat = ext16;
            } else {
                debugLog('no float color-buffer extension');
                this.dispose();
                return false;
            }

            /* EXT_float_blend gate — Codex HDR-B review fix. We use
             * additive blending into float FBOs in raw-weight summation
             * and merged-pyramid accumulation. EXT_color_buffer_float
             * alone does NOT guarantee blendable float targets; without
             * EXT_float_blend the gl.BLEND(ONE, ONE) writes silently
             * become invalid ops or no-ops on some drivers. Bail to CPU
             * fallback rather than ship wrong pixels. */
            const extFloatBlend = gl.getExtension('EXT_float_blend');
            if (!extFloatBlend) {
                debugLog('EXT_float_blend missing — additive-blend HDR passes unsafe; falling back to CPU');
                this.dispose();
                return false;
            }

            /* Need linear-filtering for FLOAT? — we use texelFetch + NEAREST
             * sampling everywhere so OES_texture_float_linear is NOT required.
             * Skip the extension probe. */

            /* Compile programs. */
            const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_FULLSCREEN);
            if (!vs) { this.dispose(); return false; }
            const fragmentSources = {
                blurH:           FRAG_BLUR_H,
                downsampleV:     FRAG_DOWNSAMPLE_V,
                blurV:           FRAG_BLUR_V,
                upsampleH:       FRAG_UPSAMPLE_H,
                weights:         FRAG_WEIGHTS,
                /* Guided-filter pipeline (Codex R1 ringing fix). */
                luminance:       FRAG_LUMINANCE_R,
                product:         FRAG_PRODUCT_R,
                medianRGBA:      FRAG_MEDIAN_RGBA,
                maxwR:           FRAG_MAXW_R,
                hybridTop:       FRAG_HYBRID_TOP,
                boxH:            FRAG_BOX_H_R,
                boxV:            FRAG_BOX_V_R,
                guidedAB:        FRAG_GUIDED_AB,
                guidedOutput:    FRAG_GUIDED_OUTPUT,
                extractChannel:  FRAG_EXTRACT_CHANNEL_R,
                copyR:           FRAG_COPY_R,
                normalize:       FRAG_NORMALIZE_W,
                laplacian:       FRAG_LAPLACIAN_DIFF,
                weightedLap:     FRAG_WEIGHTED_LAP,
                collapseAdd:     FRAG_COLLAPSE_ADD,
                finalize:        FRAG_FINALIZE,
            };
            for (const [name, src] of Object.entries(fragmentSources)) {
                const fs = compileShader(gl, gl.FRAGMENT_SHADER, src);
                if (!fs) {
                    debugLog(`shader compile failed: ${name}`);
                    gl.deleteShader(vs);
                    this.dispose();
                    return false;
                }
                const prog = linkProgram(gl, vs, fs);
                gl.deleteShader(fs);
                if (!prog) {
                    debugLog(`program link failed: ${name}`);
                    this.dispose();
                    return false;
                }
                this.programs[name] = prog;
                this.uniforms[name] = collectUniformLocations(gl, prog, [
                    'uSrc', 'uG', 'uUpNext', 'uLap', 'uWeight', 'uMerged',
                    'uUpAccum', 'uRaw', 'uSum',
                    'uA', 'uB', 'uRadius',
                    'uMeanI', 'uMeanP', 'uMeanIp', 'uMeanII', 'uEps',
                    'uGuide', 'uMeanA', 'uMeanB', 'uChannel',
                    /* Phase 2.A.3 — robust hybrid base-level. */
                    'uT0', 'uT1', 'uT2', 'uT3', 'uT4', 'uT5', 'uT6', 'uT7',
                    'uW0', 'uW1', 'uW2', 'uW3', 'uW4', 'uW5', 'uW6', 'uW7',
                    'uSoft', 'uMedian', 'uMaxW', 'uN',
                    'uSrcSize', 'uDstSize',
                    'uContrast', 'uSaturation', 'uExposure',
                    'uMuEV', 'uInv2SigEV2', 'uEvFloor', 'uWeightFloor',
                ]);
            }
            gl.deleteShader(vs);

            /* Single VAO for all draws — no attribute buffers since the vertex
             * shader uses gl_VertexID. WebGL2 still requires a VAO to be bound
             * for compliant drivers. */
            this.vao = gl.createVertexArray();

            return true;
        } catch (err) {
            debugLog('initialize threw', err);
            this.dispose();
            return false;
        }
    }

    /**
     * Mirror of CPU `fuseBrackets`. Returns same shape: { rgba, width, height }.
     * Throws on AbortSignal, unrecoverable GL state. Callers expecting a
     * fallback should run via `fuseBracketsAuto` from `../backend.js`.
     *
     * @param {Array<{rgba?:Float32Array,data?:Uint8ClampedArray|Uint8Array,width:number,height:number}>} stack
     * @param {object} [opts]
     * @returns {{ rgba: Float32Array, width: number, height: number }}
     */
    fuseBrackets(stack, opts = {}) {
        if (!this.gl) {
            throw new Error('WebGL2Backend.fuseBrackets: not initialized');
        }
        if (!Array.isArray(stack) || stack.length < 2) {
            throw new Error('WebGL2Backend.fuseBrackets: need at least 2 brackets');
        }
        if (stack.length > 8) {
            /* Capping at 8 brackets is mentioned in the brief; it also keeps
             * the per-bracket allocator predictable. */
            throw new Error('WebGL2Backend.fuseBrackets: max 8 brackets supported');
        }

        /* Phase 2.A.3: GPU robust-mode mirror landed. Camera mode is
         * unchanged (the hybrid base-level path is gated on
         * fusionMode === 'robust'). */
        const fusionMode = opts.fusionMode === 'robust' ? 'robust' : 'camera';
        const robustFloor = 3e-4;

        /* Phase 1.H: enfuse contrastMode. GPU mirror for 'stdev' is
         * deferred (Phase D in plan ticket). Throw GpuFeatureUnsupported
         * so backend.js:fuseBracketsAuto falls through to CPU for THIS
         * call only. Pattern mirrors fusionMode='robust' from 2.A.2 →
         * 2.A.3 transition. 'laplacian' (default) + 'hardmask' (silent
         * fallthrough to laplacian) are GPU-safe. */
        if (opts.contrastMode === 'stdev') {
            const e = new Error(
                'WebGL2Backend.fuseBrackets: contrastMode=\'stdev\' is CPU-only ' +
                '(GPU mirror is Phase D, deferred). Falling through to CPU.'
            );
            e.name = 'GpuFeatureUnsupported';
            throw e;
        }

        const gl = this.gl;
        const signal = opts.signal;
        const levels = clampLevels(opts.levels);
        /* Codex R1: log-luminance Gaussian about μ_EV. Defaults match
         * src/pipeline/hdr/mertens.js (DEFAULT_MU_EV = log2(0.18)). */
        const muEV    = opts.muEV    != null ? opts.muEV    : DEFAULT_MU_EV;
        const sigEV   = opts.sigEV   != null ? opts.sigEV   : DEFAULT_SIGMA_EV;
        const evFloor = opts.evFloor != null ? opts.evFloor : DEFAULT_EV_FLOOR;
        const inv2sigEV2 = 1 / (2 * sigEV * sigEV);
        const wContrast   = opts.contrast   != null ? opts.contrast   : 1;
        const wSaturation = opts.saturation != null ? opts.saturation : 1;
        const wExposure   = opts.exposure   != null ? opts.exposure   : 1;
        const skipGuided  = opts.skipGuidedFilter === true;
        const gfEps       = opts.gfEps != null ? opts.gfEps : DEFAULT_GUIDED_EPS;

        /* Validate dims + normalise to Float32 ACEScg buffers. We pass through
         * Float32 inputs verbatim and ingestSRGB(uint8) → Float32 ACEScg for
         * Uint8 inputs. fuseBracketsAuto does this too, but the GPU backend
         * is also allowed to be called directly (test does this). */
        const { width, height } = stack[0];
        for (const b of stack) {
            if (!b || !b.width || !b.height) {
                throw new Error('WebGL2Backend.fuseBrackets: invalid bracket');
            }
            if (b.width !== width || b.height !== height) {
                throw new Error('WebGL2Backend.fuseBrackets: dims mismatch');
            }
        }
        const f32Stack = stack.map((b, i) => {
            if (b.rgba instanceof Float32Array) {
                if (b.rgba.length !== width * height * 4) {
                    throw new Error(`WebGL2Backend.fuseBrackets: bracket[${i}] rgba length mismatch`);
                }
                return b.rgba;
            }
            if (b.data instanceof Uint8ClampedArray || b.data instanceof Uint8Array) {
                return ingestSRGB(b.data, b.width, b.height);
            }
            throw new Error(`WebGL2Backend.fuseBrackets: bracket[${i}] needs .rgba or .data`);
        });

        const dims = pyramidDims(width, height, levels);
        const numBrackets = f32Stack.length;

        /* Tracker for everything allocated in this call. dispose-on-throw via
         * try/finally. */
        const owned = {
            textures: [],
            framebuffers: [],
        };

        try {
            /* Resize the GL canvas to the largest target we'll render to.
             * Each draw sets gl.viewport explicitly so canvas size is mostly
             * cosmetic — but it affects the implicit drawing buffer size,
             * which on some drivers caps the active render target. */
            this.resizeCanvasIfNeeded(width, height);
            checkAbort(signal);

            /* === 1. Upload bracket textures === */
            const bracketTex = [];
            for (let k = 0; k < numBrackets; k++) {
                const tex = createRGBA32FTexture(gl, this.precision, width, height, f32Stack[k]);
                bracketTex.push(tex);
                owned.textures.push(tex);
            }
            checkAbort(signal);

            /* === 2. Per-bracket raw weights → R32F textures === */
            const rawWTex = [];
            for (let k = 0; k < numBrackets; k++) {
                const tex = createR32FTexture(gl, this.precision, width, height, null);
                rawWTex.push(tex);
                owned.textures.push(tex);
                this.passWeights(
                    bracketTex[k], width, height, tex,
                    wContrast, wSaturation, wExposure, muEV, inv2sigEV2, evFloor,
                    fusionMode === 'robust' ? robustFloor : 1e-4,
                );
                checkAbort(signal);
            }

            /* === 2.5. Guided-filter on raw weights (Codex R1 ringing fix) ===
             * filter raw → normalize once. Mirrors CPU mertens.js fuseBrackets.
             * Skipped when caller passes skipGuidedFilter:true (regression
             * tests / debug). */
            if (!skipGuided) {
                /* Guide selection — CPU side, scans f32Stack for mean
                 * log-luminance closest to muEV (or median EV when EXIF was
                 * passed via opts.evStops). */
                const guideIdx = Number.isInteger(opts.guideIndex)
                                && opts.guideIndex >= 0
                                && opts.guideIndex < f32Stack.length
                    ? opts.guideIndex
                    : selectGuideIndex(
                          f32Stack.map((rgba, i) => ({
                              rgba, width, height,
                          })),
                          opts.evStops || null, muEV, evFloor,
                      );
                /* Codex R3 STOP: clamp to [MIN_RADIUS, MAX_RADIUS] so the
                 * GPU MAX_R=32 hard-cap can never be exceeded silently. */
                const gfRadius = Number.isFinite(opts.gfRadius)
                    ? clampGuidedRadius(opts.gfRadius)
                    : defaultGuidedRadius(width, height);

                /* Guide texture = luminance of the chosen bracket. */
                const guideTex = createR32FTexture(gl, this.precision, width, height, null);
                owned.textures.push(guideTex);
                this.passLuminance(bracketTex[guideIdx], width, height, guideTex);

                /* I·I, mean(I), mean(I·I) — shared across all brackets. */
                const IITex = createR32FTexture(gl, this.precision, width, height, null);
                owned.textures.push(IITex);
                this.passProductR(guideTex, guideTex, width, height, IITex);
                const meanI  = this.boxFilter2D_R(guideTex, width, height, gfRadius, owned);
                const meanII = this.boxFilter2D_R(IITex,    width, height, gfRadius, owned);
                deleteTexture(gl, IITex);

                /* Per-bracket guided-filter pass; replaces rawWTex[k] with
                 * the filtered result. The original rawW is freed. */
                for (let k = 0; k < numBrackets; k++) {
                    const filtered = this.applyGuidedFilter(
                        rawWTex[k], guideTex, meanI, meanII,
                        width, height, gfRadius, gfEps, owned,
                    );
                    deleteTexture(gl, rawWTex[k]);
                    rawWTex[k] = filtered;
                    checkAbort(signal);
                }

                deleteTexture(gl, guideTex);
                deleteTexture(gl, meanI);
                deleteTexture(gl, meanII);
            }

            /* === 3. Sum of (filtered) weights via additive blending into sum_w === */
            const sumWTex = createR32FTexture(gl, this.precision, width, height, null);
            owned.textures.push(sumWTex);
            this.passClear(sumWTex, width, height);
            for (let k = 0; k < numBrackets; k++) {
                this.passCopyRAdd(rawWTex[k], width, height, sumWTex);
            }
            checkAbort(signal);

            /* === 4. Allocate merged Laplacian pyramid (RGBA32F per level) === */
            const merged = dims.map(d => {
                const tex = createRGBA32FTexture(gl, this.precision, d.w, d.h, null);
                owned.textures.push(tex);
                this.passClearRGBA(tex, d.w, d.h);
                return { tex, w: d.w, h: d.h };
            });

            /* Phase 2.A.3 robust-mode scratch — save coarsest gPyr +
             * coarsest wPyr per bracket so the post-loop median + maxW
             * + hybrid passes can run. Empty in camera mode. */
            const topIdx = dims.length - 1;
            const topGSaved = fusionMode === 'robust' ? new Array(numBrackets) : null;
            const topWSaved = fusionMode === 'robust' ? new Array(numBrackets) : null;

            /* === 5. Per-bracket: build normalized-weight Gaussian pyramid +
             *   RGBA Gaussian + RGBA Laplacian; accumulate into merged === */
            for (let k = 0; k < numBrackets; k++) {
                checkAbort(signal);

                /* 5a. Normalized weight at L0. */
                const nw0 = createR32FTexture(gl, this.precision, width, height, null);
                this.passNormalize(rawWTex[k], sumWTex, width, height, nw0);

                /* 5b. Gaussian pyramid of weight: chain of R32F textures. */
                const wPyr = [{ tex: nw0, w: width, h: height }];
                for (let L = 1; L < dims.length; L++) {
                    const prev = wPyr[L - 1];
                    const tmpH = createR32FTexture(gl, this.precision, prev.w, prev.h, null);
                    this.passBlurH_R(prev.tex, prev.w, prev.h, tmpH);
                    const next = createR32FTexture(gl, this.precision, dims[L].w, dims[L].h, null);
                    this.passDownsampleV_R(tmpH, prev.w, prev.h, next, dims[L].w, dims[L].h);
                    /* tmpH is consumed — free immediately. */
                    deleteTexture(gl, tmpH);
                    wPyr.push({ tex: next, w: dims[L].w, h: dims[L].h });
                }
                checkAbort(signal);

                /* 5c. Gaussian pyramid of RGBA bracket. */
                const gPyr = [{ tex: bracketTex[k], w: width, h: height, owned: false }];
                for (let L = 1; L < dims.length; L++) {
                    const prev = gPyr[L - 1];
                    const tmpH = createRGBA32FTexture(gl, this.precision, prev.w, prev.h, null);
                    this.passBlurH_RGBA(prev.tex, prev.w, prev.h, tmpH);
                    const next = createRGBA32FTexture(gl, this.precision, dims[L].w, dims[L].h, null);
                    this.passDownsampleV_RGBA(tmpH, prev.w, prev.h, next, dims[L].w, dims[L].h);
                    deleteTexture(gl, tmpH);
                    gPyr.push({ tex: next, w: dims[L].w, h: dims[L].h, owned: true });
                }
                checkAbort(signal);

                /* 5d. Build Laplacian per level: L_i = G_i - upsample(G_{i+1}, dims_i).
                 *   Top level keeps Gaussian residual (no diff). */
                const lapPyr = new Array(dims.length);
                for (let L = 0; L < dims.length - 1; L++) {
                    const dimsL = dims[L];
                    const next  = gPyr[L + 1];

                    /* Upsample G_{L+1} to dims[L] via H-pass + V-pass blur. */
                    const upH = createRGBA32FTexture(gl, this.precision, dimsL.w, dimsL.h, null);
                    this.passUpsampleH_RGBA(next.tex, next.w, next.h, upH, dimsL.w, dimsL.h);
                    const upFull = createRGBA32FTexture(gl, this.precision, dimsL.w, dimsL.h, null);
                    this.passBlurV_RGBA(upH, dimsL.w, dimsL.h, upFull);
                    deleteTexture(gl, upH);

                    /* Diff = G_L - upsampled(G_{L+1}). */
                    const lapL = createRGBA32FTexture(gl, this.precision, dimsL.w, dimsL.h, null);
                    this.passLaplacianDiff(gPyr[L].tex, upFull, dimsL.w, dimsL.h, lapL);
                    deleteTexture(gl, upFull);
                    lapPyr[L] = { tex: lapL, w: dimsL.w, h: dimsL.h, owned: true };
                }
                /* Top of Laplacian = top of Gaussian (residual). Borrow ref;
                 * we'll free via gPyr ownership below. */
                const top = dims.length - 1;
                lapPyr[top] = { tex: gPyr[top].tex, w: gPyr[top].w, h: gPyr[top].h, owned: false };

                /* 5e. Accumulate weighted-Laplacian into merged pyramid. */
                for (let L = 0; L < dims.length; L++) {
                    this.passWeightedLapAccumulate(
                        lapPyr[L].tex, wPyr[L].tex, dims[L].w, dims[L].h, merged[L].tex,
                    );
                }
                checkAbort(signal);

                /* Robust mode: retain coarsest-level gPyr + wPyr per
                 * bracket. We pull the textures OUT of the pyramid so
                 * they don't get freed in 5f below; new tracking entry
                 * stays in owned.textures so the catch path still
                 * cleans up. */
                if (fusionMode === 'robust') {
                    topGSaved[k] = gPyr[topIdx].tex;
                    gPyr[topIdx].tex = null;             /* remove from free list */
                    topWSaved[k] = wPyr[topIdx].tex;
                    wPyr[topIdx].tex = null;
                }

                /* 5f. Free this bracket's pyramid resources. */
                for (let L = 0; L < wPyr.length; L++) {
                    if (wPyr[L].tex) deleteTexture(gl, wPyr[L].tex);
                }
                /* gPyr[0] reuses bracketTex — don't free; we'll free bracketTex[k] now. */
                for (let L = 1; L < gPyr.length; L++) {
                    if (gPyr[L].tex) deleteTexture(gl, gPyr[L].tex);
                }
                for (let L = 0; L < lapPyr.length; L++) {
                    if (lapPyr[L].owned) deleteTexture(gl, lapPyr[L].tex);
                }
                deleteTexture(gl, bracketTex[k]);
                bracketTex[k] = null;
                /* Don't remove from owned.textures — disposal in catch path
                 * tolerates double-free via deleteTexture's null-guard. */
            }

            /* === 5.5. Phase 2.A.3 robust hybrid base-level blend.
             * In robust mode, replace merged[top] with:
             *   final_top = lerp(soft_blend_top, median_top, mix(maxW, N))
             * where median_top is per-pixel median of the coarsest
             * Gaussian-of-bracket and maxW is per-pixel max of the
             * coarsest normalized weights. Mirrors CPU mertens.js. */
            if (fusionMode === 'robust' && topGSaved && topWSaved) {
                checkAbort(signal);
                const td = dims[topIdx];
                /* median of coarsest gPyrs. */
                const medianTopTex = createRGBA32FTexture(gl, this.precision, td.w, td.h, null);
                owned.textures.push(medianTopTex);
                this.passMedianRGBA(topGSaved, numBrackets, td.w, td.h, medianTopTex);
                /* maxW of coarsest wPyrs. */
                const maxwTopTex = createR32FTexture(gl, this.precision, td.w, td.h, null);
                owned.textures.push(maxwTopTex);
                this.passMaxWR(topWSaved, numBrackets, td.w, td.h, maxwTopTex);
                /* hybrid lerp into a fresh tex, then swap into merged[top]. */
                const finalTopTex = createRGBA32FTexture(gl, this.precision, td.w, td.h, null);
                owned.textures.push(finalTopTex);
                this.passHybridTop(merged[topIdx].tex, medianTopTex, maxwTopTex,
                                   numBrackets, td.w, td.h, finalTopTex);
                /* Replace merged[top] with the hybrid result. The old
                 * merged[top].tex stays in owned.textures so the
                 * cleanup path frees it; we just swap the reference. */
                deleteTexture(gl, merged[topIdx].tex);
                merged[topIdx].tex = finalTopTex;
                /* Free per-bracket scratch. */
                for (let k = 0; k < numBrackets; k++) {
                    if (topGSaved[k]) deleteTexture(gl, topGSaved[k]);
                    if (topWSaved[k]) deleteTexture(gl, topWSaved[k]);
                }
                deleteTexture(gl, medianTopTex);
                deleteTexture(gl, maxwTopTex);
                checkAbort(signal);
            }

            /* === 6. Collapse merged pyramid coarsest → finest. === */
            checkAbort(signal);
            let cur = merged[merged.length - 1].tex;
            let cw  = merged[merged.length - 1].w;
            let ch  = merged[merged.length - 1].h;
            let curOwned = false;   // first cur is the merged top — freed via owned list
            for (let L = merged.length - 2; L >= 0; L--) {
                checkAbort(signal);
                const next = merged[L];
                /* Upsample cur → next dims. */
                const upH = createRGBA32FTexture(gl, this.precision, next.w, next.h, null);
                this.passUpsampleH_RGBA(cur, cw, ch, upH, next.w, next.h);
                const upFull = createRGBA32FTexture(gl, this.precision, next.w, next.h, null);
                this.passBlurV_RGBA(upH, next.w, next.h, upFull);
                deleteTexture(gl, upH);

                /* sum = next + upFull, written to a fresh tex (we want to keep
                 * `merged[L].tex` immutable in case the caller's signal interrupts;
                 * the alloc cost is one RGBA32F per level which is small). */
                const sumTex = createRGBA32FTexture(gl, this.precision, next.w, next.h, null);
                this.passCollapseAdd(next.tex, upFull, next.w, next.h, sumTex);
                deleteTexture(gl, upFull);

                if (curOwned) deleteTexture(gl, cur);
                cur = sumTex;
                cw = next.w;
                ch = next.h;
                curOwned = true;
            }

            /* === 7. Force alpha = 1 on final L0. === */
            const finalTex = createRGBA32FTexture(gl, this.precision, cw, ch, null);
            this.passFinalize(cur, cw, ch, finalTex);
            if (curOwned) deleteTexture(gl, cur);
            cur = finalTex;
            curOwned = true;

            /* === 8. Read back to Float32. === */
            const rgba = this.readPixelsFloat(cur, cw, ch);
            if (curOwned) deleteTexture(gl, cur);

            return { rgba, width: cw, height: ch };
        } finally {
            /* Best-effort cleanup of anything we registered. Individual delete
             * sites above are already careful, but a thrown error mid-build
             * (incl. AbortError) will get cleaned up here. */
            for (const fb of owned.framebuffers) {
                try { gl.deleteFramebuffer(fb); } catch (_) { /* ignore */ }
            }
            for (const tex of owned.textures) {
                if (tex) { try { gl.deleteTexture(tex); } catch (_) { /* ignore */ } }
            }
        }
    }

    /**
     * Release shared resources (programs, VAO). Call on backend tear-down or
     * on initialize() failure. Per-call resources are managed by fuseBrackets
     * itself via try/finally — no need to track them here.
     */
    dispose() {
        if (this.disposed) return;
        const gl = this.gl;
        if (gl) {
            for (const prog of Object.values(this.programs)) {
                try { gl.deleteProgram(prog); } catch (_) { /* ignore */ }
            }
            this.programs = {};
            this.uniforms = {};
            if (this.vao) {
                try { gl.deleteVertexArray(this.vao); } catch (_) { /* ignore */ }
                this.vao = null;
            }
        }
        this.gl = null;
        this.disposed = true;
    }

    /* ── Render-pass primitives. Each binds a program, sets uniforms, draws. ─ */

    resizeCanvasIfNeeded(w, h) {
        const c = this.canvas;
        if (!c) return;
        if (c.width !== w || c.height !== h) {
            c.width = w;
            c.height = h;
        }
    }

    /**
     * Bind one of our shader programs and the shared full-screen VAO; set the
     * viewport to the (dstW, dstH) we're rendering to.
     */
    bindOp(name, dstW, dstH) {
        const gl = this.gl;
        gl.useProgram(this.programs[name]);
        gl.bindVertexArray(this.vao);
        gl.viewport(0, 0, dstW, dstH);
    }

    /**
     * Bind the destination texture as the current framebuffer's color
     * attachment; create + delete a temporary FBO. Reusing FBOs across passes
     * shaved a few µs in profiling but added management complexity — current
     * approach is to allocate inside each pass and free immediately.
     */
    bindTextureAsFBO(tex) {
        const gl = this.gl;
        const fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
        return fb;
    }

    setSamplerSlot(progName, uniformName, slot, tex) {
        const gl = this.gl;
        gl.activeTexture(gl.TEXTURE0 + slot);
        gl.bindTexture(gl.TEXTURE_2D, tex);
        const loc = this.uniforms[progName][uniformName];
        if (loc) gl.uniform1i(loc, slot);
    }

    /* Pass: per-pixel weights into single-channel R32F.
     * Codex R1: log-luminance Gaussian (uMuEV, uInv2SigEV2) replaces the
     * prior linear-domain per-channel μ.
     * Phase 2.A.3: weightFloor uniform — robust mode lifts to 3e-4. */
    passWeights(srcTex, w, h, dstTex, contrast, saturation, exposure, muEV, inv2sigEV2, evFloor, weightFloor) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('weights', w, h);
        this.setSamplerSlot('weights', 'uSrc', 0, srcTex);
        gl.uniform2i(this.uniforms.weights.uSrcSize, w, h);
        gl.uniform1f(this.uniforms.weights.uContrast, contrast);
        gl.uniform1f(this.uniforms.weights.uSaturation, saturation);
        gl.uniform1f(this.uniforms.weights.uExposure, exposure);
        gl.uniform1f(this.uniforms.weights.uMuEV, muEV);
        gl.uniform1f(this.uniforms.weights.uInv2SigEV2, inv2sigEV2);
        gl.uniform1f(this.uniforms.weights.uEvFloor, evFloor);
        gl.uniform1f(this.uniforms.weights.uWeightFloor, weightFloor != null ? weightFloor : 1e-4);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* ── Phase 2.A.3 — robust hybrid base-level passes ──────────────── */

    /* Median of N RGBA bracket textures into a fresh RGBA32F. N ≤ 8. */
    passMedianRGBA(bracketTops, n, w, h, dstTex) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('medianRGBA', w, h);
        for (let k = 0; k < 8; k++) {
            const u = this.uniforms.medianRGBA['uT' + k];
            if (!u) continue;
            const tex = k < n ? bracketTops[k] : bracketTops[0];   /* dummy fill */
            gl.activeTexture(gl.TEXTURE0 + k);
            gl.bindTexture(gl.TEXTURE_2D, tex);
            gl.uniform1i(u, k);
        }
        gl.uniform1i(this.uniforms.medianRGBA.uN, n);
        gl.uniform2i(this.uniforms.medianRGBA.uSrcSize, w, h);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* Max of N R32F weight textures into a fresh R32F. N ≤ 8. */
    passMaxWR(weightTops, n, w, h, dstTex) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('maxwR', w, h);
        for (let k = 0; k < 8; k++) {
            const u = this.uniforms.maxwR['uW' + k];
            if (!u) continue;
            const tex = k < n ? weightTops[k] : weightTops[0];
            gl.activeTexture(gl.TEXTURE0 + k);
            gl.bindTexture(gl.TEXTURE_2D, tex);
            gl.uniform1i(u, k);
        }
        gl.uniform1i(this.uniforms.maxwR.uN, n);
        gl.uniform2i(this.uniforms.maxwR.uSrcSize, w, h);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* Hybrid lerp: dst = lerp(soft, median, mix(maxW, N)). RGBA32F. */
    passHybridTop(softTex, medianTex, maxwTex, n, w, h, dstTex) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('hybridTop', w, h);
        this.setSamplerSlot('hybridTop', 'uSoft',   0, softTex);
        this.setSamplerSlot('hybridTop', 'uMedian', 1, medianTex);
        this.setSamplerSlot('hybridTop', 'uMaxW',   2, maxwTex);
        gl.uniform1i(this.uniforms.hybridTop.uN, n);
        gl.uniform2i(this.uniforms.hybridTop.uSrcSize, w, h);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* ── Guided-filter passes (Codex R1) ──────────────────────────── */

    /* Pass: lum(I) = Rec.709 luminance from RGBA → R32F. */
    passLuminance(srcTex, w, h, dstTex) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('luminance', w, h);
        this.setSamplerSlot('luminance', 'uSrc', 0, srcTex);
        gl.uniform2i(this.uniforms.luminance.uSrcSize, w, h);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* Pass: dst.r = a.r * b.r (element-wise product of two R32F textures). */
    passProductR(aTex, bTex, w, h, dstTex) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('product', w, h);
        this.setSamplerSlot('product', 'uA', 0, aTex);
        this.setSamplerSlot('product', 'uB', 1, bTex);
        gl.uniform2i(this.uniforms.product.uSrcSize, w, h);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* Box filter passes — separable 1D moving-average with dynamic radius.
     * Boundary: out-of-bounds taps skipped, divisor = actual count. */
    passBoxH(srcTex, w, h, dstTex, radius) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('boxH', w, h);
        this.setSamplerSlot('boxH', 'uSrc', 0, srcTex);
        gl.uniform2i(this.uniforms.boxH.uSrcSize, w, h);
        gl.uniform1i(this.uniforms.boxH.uRadius, radius);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }
    passBoxV(srcTex, w, h, dstTex, radius) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('boxV', w, h);
        this.setSamplerSlot('boxV', 'uSrc', 0, srcTex);
        gl.uniform2i(this.uniforms.boxV.uSrcSize, w, h);
        gl.uniform1i(this.uniforms.boxV.uRadius, radius);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* Pass: guided-filter (a, b) coefficients packed into R=a, G=b.
     * Textbook moments: cov = mean(I·p) − mean(I)·mean(p), varI = mean(I²) − mean(I)². */
    passGuidedAB(meanI, meanP, meanIp, meanII, w, h, dstTex, eps) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('guidedAB', w, h);
        this.setSamplerSlot('guidedAB', 'uMeanI',  0, meanI);
        this.setSamplerSlot('guidedAB', 'uMeanP',  1, meanP);
        this.setSamplerSlot('guidedAB', 'uMeanIp', 2, meanIp);
        this.setSamplerSlot('guidedAB', 'uMeanII', 3, meanII);
        gl.uniform2i(this.uniforms.guidedAB.uSrcSize, w, h);
        gl.uniform1f(this.uniforms.guidedAB.uEps, eps);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* Pass: q = mean(a)·I + mean(b). Three R32F inputs, R32F output. */
    passGuidedOutput(guideTex, meanATex, meanBTex, w, h, dstTex) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('guidedOutput', w, h);
        this.setSamplerSlot('guidedOutput', 'uGuide', 0, guideTex);
        this.setSamplerSlot('guidedOutput', 'uMeanA', 1, meanATex);
        this.setSamplerSlot('guidedOutput', 'uMeanB', 2, meanBTex);
        gl.uniform2i(this.uniforms.guidedOutput.uSrcSize, w, h);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* Pass: extract one channel from RGBA into R32F. */
    passExtractChannel(srcTex, channel, w, h, dstTex) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('extractChannel', w, h);
        this.setSamplerSlot('extractChannel', 'uSrc', 0, srcTex);
        gl.uniform2i(this.uniforms.extractChannel.uSrcSize, w, h);
        gl.uniform1i(this.uniforms.extractChannel.uChannel, channel);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* Helper: separable 2D box filter (H then V). Allocates and returns a
     * new R32F texture; tracks intermediate H-pass in `owned`. The H-pass
     * texture is deleted before return to keep peak memory low. */
    boxFilter2D_R(srcTex, w, h, radius, owned) {
        const gl = this.gl;
        const tmpH = createR32FTexture(gl, this.precision, w, h, null);
        owned.textures.push(tmpH);
        this.passBoxH(srcTex, w, h, tmpH, radius);
        const dst = createR32FTexture(gl, this.precision, w, h, null);
        owned.textures.push(dst);
        this.passBoxV(tmpH, w, h, dst, radius);
        deleteTexture(gl, tmpH);
        return dst;
    }

    /* Helper: full guided-filter pass on a single weight map.
     * meanI / meanII are pre-computed (shared across brackets — both
     * depend only on the guide image). Returns a new R32F filtered
     * weight texture; intermediates tracked in `owned`. Mirrors the
     * textbook formulation in CPU guided-filter.js. */
    applyGuidedFilter(rawW, guideTex, meanI, meanII, w, h, radius, eps, owned) {
        const gl = this.gl;
        /* mean(p) */
        const meanP = this.boxFilter2D_R(rawW, w, h, radius, owned);
        /* I·p, then mean(I·p) */
        const Ip = createR32FTexture(gl, this.precision, w, h, null);
        owned.textures.push(Ip);
        this.passProductR(guideTex, rawW, w, h, Ip);
        const meanIp = this.boxFilter2D_R(Ip, w, h, radius, owned);
        deleteTexture(gl, Ip);
        /* (a, b) packed in RG channels of an RGBA32F. */
        const ab = createRGBA32FTexture(gl, this.precision, w, h, null);
        owned.textures.push(ab);
        this.passGuidedAB(meanI, meanP, meanIp, meanII, w, h, ab, eps);
        deleteTexture(gl, meanP);
        deleteTexture(gl, meanIp);
        /* Extract a, b. */
        const aTex = createR32FTexture(gl, this.precision, w, h, null);
        owned.textures.push(aTex);
        this.passExtractChannel(ab, 0, w, h, aTex);
        const bTex = createR32FTexture(gl, this.precision, w, h, null);
        owned.textures.push(bTex);
        this.passExtractChannel(ab, 1, w, h, bTex);
        deleteTexture(gl, ab);
        /* mean(a), mean(b). */
        const meanA = this.boxFilter2D_R(aTex, w, h, radius, owned);
        deleteTexture(gl, aTex);
        const meanB = this.boxFilter2D_R(bTex, w, h, radius, owned);
        deleteTexture(gl, bTex);
        /* q = mean(a)·I + mean(b). */
        const filtered = createR32FTexture(gl, this.precision, w, h, null);
        owned.textures.push(filtered);
        this.passGuidedOutput(guideTex, meanA, meanB, w, h, filtered);
        deleteTexture(gl, meanA);
        deleteTexture(gl, meanB);
        return filtered;
    }

    /* Pass: clear a single-channel target to (0,0,0,0). */
    passClear(dstTex, w, h) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.viewport(0, 0, w, h);
        gl.disable(gl.BLEND);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    passClearRGBA(dstTex, w, h) {
        /* Same physical operation; kept distinct for symmetry / readability. */
        this.passClear(dstTex, w, h);
    }

    /* Pass: additive copy (additively blend src.r into dst.r). */
    passCopyRAdd(srcTex, w, h, dstTex) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE);
        this.bindOp('copyR', w, h);
        this.setSamplerSlot('copyR', 'uSrc', 0, srcTex);
        gl.uniform2i(this.uniforms.copyR.uSrcSize, w, h);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.disable(gl.BLEND);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* Pass: divide raw / (sum + EPS). */
    passNormalize(rawTex, sumTex, w, h, dstTex) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('normalize', w, h);
        this.setSamplerSlot('normalize', 'uRaw', 0, rawTex);
        this.setSamplerSlot('normalize', 'uSum', 1, sumTex);
        gl.uniform2i(this.uniforms.normalize.uSrcSize, w, h);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* H-blur on R32F. */
    passBlurH_R(srcTex, w, h, dstTex) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('blurH', w, h);
        this.setSamplerSlot('blurH', 'uSrc', 0, srcTex);
        gl.uniform2i(this.uniforms.blurH.uSrcSize, w, h);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* V-blur+stride2 on R32F. */
    passDownsampleV_R(srcTex, sw, sh, dstTex, dw, dh) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('downsampleV', dw, dh);
        this.setSamplerSlot('downsampleV', 'uSrc', 0, srcTex);
        gl.uniform2i(this.uniforms.downsampleV.uSrcSize, sw, sh);
        gl.uniform2i(this.uniforms.downsampleV.uDstSize, dw, dh);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* H-blur on RGBA32F. */
    passBlurH_RGBA(srcTex, w, h, dstTex) {
        this.passBlurH_R(srcTex, w, h, dstTex);
    }

    /* V-blur+stride2 on RGBA32F. */
    passDownsampleV_RGBA(srcTex, sw, sh, dstTex, dw, dh) {
        this.passDownsampleV_R(srcTex, sw, sh, dstTex, dw, dh);
    }

    /* Upsample H-pass on RGBA32F (zero-insert + ×4 + 5-tap). Renders at (dw,dh). */
    passUpsampleH_RGBA(srcTex, sw, sh, dstTex, dw, dh) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('upsampleH', dw, dh);
        this.setSamplerSlot('upsampleH', 'uSrc', 0, srcTex);
        gl.uniform2i(this.uniforms.upsampleH.uSrcSize, sw, sh);
        gl.uniform2i(this.uniforms.upsampleH.uDstSize, dw, dh);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* V-blur full size on RGBA32F. (Used as the second pass of upsample.) */
    passBlurV_RGBA(srcTex, w, h, dstTex) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('blurV', w, h);
        this.setSamplerSlot('blurV', 'uSrc', 0, srcTex);
        gl.uniform2i(this.uniforms.blurV.uSrcSize, w, h);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* Pass: dst = G - upsampled(G_next). */
    passLaplacianDiff(gTex, upTex, w, h, dstTex) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('laplacian', w, h);
        this.setSamplerSlot('laplacian', 'uG', 0, gTex);
        this.setSamplerSlot('laplacian', 'uUpNext', 1, upTex);
        gl.uniform2i(this.uniforms.laplacian.uSrcSize, w, h);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* Pass: merged += lap * w. Uses additive blending. */
    passWeightedLapAccumulate(lapTex, wTex, w, h, mergedTex) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(mergedTex);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE);
        this.bindOp('weightedLap', w, h);
        this.setSamplerSlot('weightedLap', 'uLap', 0, lapTex);
        this.setSamplerSlot('weightedLap', 'uWeight', 1, wTex);
        gl.uniform2i(this.uniforms.weightedLap.uSrcSize, w, h);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.disable(gl.BLEND);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* Pass: dst = merged + upAccum. */
    passCollapseAdd(mergedTex, upTex, w, h, dstTex) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('collapseAdd', w, h);
        this.setSamplerSlot('collapseAdd', 'uMerged', 0, mergedTex);
        this.setSamplerSlot('collapseAdd', 'uUpAccum', 1, upTex);
        gl.uniform2i(this.uniforms.collapseAdd.uSrcSize, w, h);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    passFinalize(srcTex, w, h, dstTex) {
        const gl = this.gl;
        const fb = this.bindTextureAsFBO(dstTex);
        gl.disable(gl.BLEND);
        this.bindOp('finalize', w, h);
        this.setSamplerSlot('finalize', 'uSrc', 0, srcTex);
        gl.uniform2i(this.uniforms.finalize.uSrcSize, w, h);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /* Final readback of an RGBA texture into a fresh Float32Array. */
    readPixelsFloat(tex, w, h) {
        const gl = this.gl;
        const fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
        const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
        if (status !== gl.FRAMEBUFFER_COMPLETE) {
            gl.bindFramebuffer(gl.FRAMEBUFFER, null);
            gl.deleteFramebuffer(fb);
            throw new Error('WebGL2Backend.readPixelsFloat: incomplete FBO ' + status);
        }
        const out = new Float32Array(w * h * 4);
        /* gl.flush keeps WebGL pipelining tight; readPixels is synchronous in
         * WebGL2 so we don't need a fence. */
        gl.flush();
        gl.readPixels(0, 0, w, h, gl.RGBA, gl.FLOAT, out);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
        return out;
    }
}

/* ── Helpers ────────────────────────────────────────────────────────────── */

function checkAbort(signal) {
    if (signal && signal.aborted) {
        const err = new Error('WebGL2Backend.fuseBrackets: aborted');
        err.name = 'AbortError';
        throw err;
    }
}

function compileShader(gl, kind, src) {
    const sh = gl.createShader(kind);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        debugLog('shader compile log:', gl.getShaderInfoLog(sh));
        gl.deleteShader(sh);
        return null;
    }
    return sh;
}

function linkProgram(gl, vs, fs) {
    const p = gl.createProgram();
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
        debugLog('program link log:', gl.getProgramInfoLog(p));
        gl.deleteProgram(p);
        return null;
    }
    return p;
}

function collectUniformLocations(gl, prog, names) {
    const out = {};
    for (const n of names) {
        out[n] = gl.getUniformLocation(prog, n);
    }
    return out;
}

/**
 * Allocate an RGBA texture in the chosen float precision. Pass `data` as a
 * Float32Array to upload, or null to leave uninitialised (we typically clear
 * via passClear before sampling). For 16-bit fallback we still upload the
 * Float32Array — WebGL2 handles the format conversion on upload.
 */
function createRGBA32FTexture(gl, precision, w, h, data) {
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    if (precision === '16f') {
        /* RGBA16F internal format with FLOAT data path is permitted in WebGL2. */
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, w, h, 0, gl.RGBA, gl.FLOAT, data);
    } else {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, w, h, 0, gl.RGBA, gl.FLOAT, data);
    }
    gl.bindTexture(gl.TEXTURE_2D, null);
    return tex;
}

function createR32FTexture(gl, precision, w, h, data) {
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    if (precision === '16f') {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.R16F, w, h, 0, gl.RED, gl.FLOAT, data);
    } else {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.R32F, w, h, 0, gl.RED, gl.FLOAT, data);
    }
    gl.bindTexture(gl.TEXTURE_2D, null);
    return tex;
}

function deleteTexture(gl, tex) {
    if (!tex) return;
    try { gl.deleteTexture(tex); } catch (_) { /* ignore */ }
}

function createOffscreenCanvas(w, h) {
    if (typeof OffscreenCanvas !== 'undefined') {
        return new OffscreenCanvas(w, h);
    }
    if (typeof document !== 'undefined' && document.createElement) {
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        return c;
    }
    throw new Error('WebGL2Backend: no canvas runtime available');
}
